import { useEffect } from 'react';

import { getCombinedIntegrationClassName } from '../../utils/css-utils';
import { attachSnippetResize, createSnippetIframe } from '../../components/sandboxed-html/snippet-iframe';

/**
 * Converts html-snippet marker divs into sandboxed iframes. The snippet html is
 * delivered by the server inside an inert <template>, so it is never parsed into
 * the live page; we read that template string and render it in an opaque-origin
 * iframe (no access to the host page). Markers already converted are skipped.
 *
 * @param container - element that contains the rendered answer html
 */
export function initializeHtmlSnippets(container: HTMLElement): void {
  const markers = container.querySelectorAll(
    getCombinedIntegrationClassName('-answer-item.html-snippet'),
  );

  markers.forEach((marker) => {
    const markerEl = marker as HTMLElement;

    // already converted on a previous run
    if (markerEl.querySelector('iframe') != null) {
      return;
    }

    // read the inert payload; fall back to the marker's own html for safety
    const template = markerEl.querySelector('template') as HTMLTemplateElement | null;
    const html = template != null ? template.innerHTML : markerEl.innerHTML;

    const { iframe, nonce } = createSnippetIframe(html);
    attachSnippetResize(iframe, nonce);

    markerEl.innerHTML = '';
    markerEl.appendChild(iframe);
  });
}

/**
 * Hook to isolate html-snippet elements in rendered answers.
 * Runs after every render: when the answer html changes, React replaces the
 * innerHTML wholesale and the fresh markers need to be initialized again.
 * The dependency array is omitted on purpose — [ref] (as used by
 * useAnswerCodeEditor) would skip re-runs on html changes because the ref
 * object itself is stable.
 *
 * @param ref - reference to the html element that contains the answer
 */
export function useHtmlSnippet(ref: { current: HTMLElement | null }): void {
  useEffect(() => {
    if (ref.current == null) {
      return;
    }

    initializeHtmlSnippets(ref.current);
  });
}
